/* eslint-disable max-lines */
import {
  useAlarmMedia,
  useDoorCameraDetails,
  useTenantDetails,
} from '@hakimo-ui/hakimo/data-access';
import { environment } from '@hakimo-ui/hakimo/environments';
import { Alarm, DataVersion } from '@hakimo-ui/hakimo/types';
import { BasicPanel, LoadingIndicator } from '@hakimo-ui/hakimo/ui-elements';
import {
  isUnifiedDataVersion,
  useCanViewLiveCamera,
} from '@hakimo-ui/hakimo/util';
import * as Sentry from '@sentry/react';
import { useState } from 'react';
import { LiveView } from './LiveView';
import AlarmMediaPlayer from './AlarmMediaPlayer';
import AlarmMediaActions from './actions/AlarmMediaActions';
import {
  RgDownloadButton,
  ScanDownloadButton,
} from './actions/DownloadMediaButton';
import { getMediaRefetchInterval } from './alarm-video/util';

interface Props {
  sharedToken?: string;
  alarm: Alarm;
  onTalkdown: (cameraId: string, alarmId?: string) => void;
  dataVersion?: DataVersion;
}

const isAirgap = environment.name === 'AIRGAP';

export function AlarmMedia(props: Props) {
  const { alarm, sharedToken, onTalkdown, dataVersion } = props;
  const [debug, setDebug] = useState(false);
  const canViewLiveVideo = useCanViewLiveCamera();

  const { data: tenantData } = useTenantDetails(alarm.tenant?.id ?? '');
  const disableMediaDownload =
    Boolean(sharedToken) ||
    (tenantData ? tenantData.config?.disableMediaDownload ?? false : true);

  const queryResult = useAlarmMedia({
    id: alarm.id,
    debug,
    // includeBoxes=true so scan-style frames carry persons/vehicles for
    // FramePlayer's overlays. AlarmVideo doesn't consume them.
    boxes: isUnifiedDataVersion(dataVersion),
    sharedToken,
    refetchInterval: (data) => getMediaRefetchInterval(data, isAirgap),
    timestamp: new Date(alarm.timestamp).getTime(),
    dataVersion,
  });

  // The media response is authoritative: `alarm.media` comes from the
  // detail/list payload, which does not always embed media. Drives only the
  // player and download button; the page's action set keys off product.
  const hasFrames = Boolean(queryResult.data?.media?.frames?.length);

  const [live, setLive] = useState(false);

  const onErrorLoadingVideo = () => {
    Sentry.captureMessage('Error loading video', {
      extra: { alarm },
    });
  };

  const isDoorAlarm = alarm.sourceEntity?.type === 'DOOR';
  const doorId = isDoorAlarm ? alarm.sourceEntity?.id || null : null;
  const directCameraId =
    alarm.sourceEntity?.type === 'CAMERA' ? alarm.sourceEntity.id : null;

  const shouldFetchDoorCamera = isDoorAlarm && doorId && live;
  const doorCameraQuery = useDoorCameraDetails(
    shouldFetchDoorCamera ? doorId : null,
    undefined
  );

  const cameraId =
    directCameraId ??
    (shouldFetchDoorCamera && doorCameraQuery.data
      ? doorCameraQuery.data.cameraId
      : null);
  const isLoadingDoorCamera =
    shouldFetchDoorCamera && doorCameraQuery.isLoading;

  const renderContent = (
    <>
      {cameraId && canViewLiveVideo && live && (
        <LiveView
          cameraId={cameraId}
          doorCameraDetails={
            shouldFetchDoorCamera && doorCameraQuery.data
              ? {
                  cameraId: doorCameraQuery.data.cameraId,
                  livestreamUrl: doorCameraQuery.data.livestreamUrl,
                }
              : undefined
          }
        />
      )}
      {isLoadingDoorCamera && live && (
        <div className="flex h-full w-full items-center justify-center">
          <LoadingIndicator text="Loading..." />
        </div>
      )}
      {!live && (
        <AlarmMediaPlayer
          alarm={alarm}
          queryResult={queryResult}
          onErrorLoadingVideo={onErrorLoadingVideo}
          disableDownload={disableMediaDownload}
          dataVersion={dataVersion}
        />
      )}
    </>
  );

  // v1 is authed views only
  const downloadAction =
    sharedToken || disableMediaDownload ? null : hasFrames ? (
      <ScanDownloadButton alarmId={alarm.id} dataVersion={dataVersion} />
    ) : (
      <RgDownloadButton
        alarmId={alarm.id}
        mediaUrl={queryResult.data?.videoPath ?? queryResult.data?.imagePath}
        debug={debug}
      />
    );

  const actions = (
    <AlarmMediaActions
      product={hasFrames ? 'scan' : 'rg'}
      cameraId={cameraId}
      alarmId={alarm.id}
      debug={debug}
      live={live}
      sharedToken={sharedToken}
      downloadAction={downloadAction}
      onToggleDebug={setDebug}
      onToggleLive={setLive}
      onTalkdown={onTalkdown}
    />
  );

  return (
    <BasicPanel title="Alarm Media" secondary={actions}>
      {renderContent}
    </BasicPanel>
  );
}

export default AlarmMedia;
